import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  GiftIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import RewardCardVisual from '../shared/RewardCardVisual';
import AnimatedStatNumber from './AnimatedStatNumber';
import Button from '../ui/Button';
import PromotionsIntroModal from './PromotionsIntroModal';

function WelcomeRewardOption({ reward, selected, onSelect, formatMessage, pointsBadgeKey }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(reward.id)}
      aria-pressed={selected}
      className={`group w-full overflow-hidden rounded-xl border text-left transition-all duration-200 ${
        selected
          ? 'border-orange-500 bg-white shadow-[0_12px_32px_rgba(249,115,22,0.14)] ring-1 ring-orange-500/30 '
          : 'border-secondary-200/80 bg-white hover:border-secondary-300 hover:shadow-soft   '
      }`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-secondary-950">
        <RewardCardVisual reward={reward} />
        {selected ? (
          <span className="absolute right-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white shadow-sm">
            <CheckCircleIcon className="h-4 w-4" />
          </span>
        ) : null}
      </div>
      <div className="border-t border-secondary-100 px-4 py-3 ">
        <div className="flex items-start justify-between gap-3">
          <p className="line-clamp-1 text-sm font-semibold leading-snug text-secondary-900  sm:text-base">
            {reward.name}
          </p>
          <span className="shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-secondary-700   sm:text-xs">
            {formatMessage(pointsBadgeKey, { count: reward.points_required || 0 })}
          </span>
        </div>
      </div>
    </button>
  );
}

function StepDots({ current, className = '' }) {
  return (
    <div className={`mb-4 flex items-center justify-center gap-2 ${className}`} aria-hidden="true">
      {[1, 2].map((step) => (
        <span
          key={step}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            step === current
              ? 'w-6 bg-orange-500'
              : step < current
                ? 'w-1.5 bg-orange-300'
                : 'w-1.5 bg-secondary-200 '
          }`}
        />
      ))}
    </div>
  );
}

function ClientWelcomeOnboarding({ username, onComplete, onRedeem, onViewPromotions }) {
  const { t, formatMessage } = useLanguage();
  const w = t.welcomeOnboarding;
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [selectedRewardId, setSelectedRewardId] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const [showPromotionsIntro, setShowPromotionsIntro] = useState(false);
  const [redeemedReward, setRedeemedReward] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const confettiTimerRef = useRef(null);

  const fireConfetti = useCallback(() => {
    if (confettiTimerRef.current) {
      clearTimeout(confettiTimerRef.current);
    }
    setConfettiActive(true);
    confettiTimerRef.current = setTimeout(() => {
      setConfettiActive(false);
    }, 2800);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    return () => {
      if (confettiTimerRef.current) {
        clearTimeout(confettiTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadWelcomeData() {
      try {
        const response = await axios.get('/api/client/welcome-onboarding', {
          withCredentials: true,
        });
        if (cancelled) {
          return;
        }
        if (response.data?.show_onboarding) {
          const welcomeRewards = response.data.welcome_rewards?.length
            ? response.data.welcome_rewards
            : response.data.welcome_reward
              ? [response.data.welcome_reward]
              : [];
          setData({ ...response.data, welcome_rewards: welcomeRewards });
          setSelectedRewardId(welcomeRewards[0]?.id ?? null);
          setStep(1);
        } else {
          onComplete?.();
        }
      } catch (error) {
        console.error('Error loading welcome onboarding:', error);
        onComplete?.();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadWelcomeData();
    return () => {
      cancelled = true;
    };
  }, [onComplete]);

  const finishOnboarding = async (goToRewards = false, redeemedReward = null) => {
    try {
      await axios.post('/api/client/welcome-onboarding/complete', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error completing welcome onboarding:', error);
    }
    onComplete?.();
    if (goToRewards) {
      onRedeem?.(redeemedReward);
    }
  };

  const handleAcceptWelcome = () => {
    setStep(2);
  };

  const handleRedeemNow = async () => {
    if (!selectedRewardId) {
      toast.error(w.selectReward);
      return;
    }

    const rewardToRedeem = (data.welcome_rewards || []).find(
      (reward) => reward.id === selectedRewardId
    );

    setClaiming(true);
    try {
      await axios.post(
        `/api/client/rewards/${selectedRewardId}/claim`,
        {},
        { withCredentials: true }
      );
      fireConfetti();
      // Show the promotions intro next; it finalizes the onboarding on close.
      setRedeemedReward(rewardToRedeem || null);
      setShowPromotionsIntro(true);
    } catch (error) {
      console.error('Error claiming welcome reward:', error);
      toast.error(error.response?.data?.error || w.claimFailed);
    } finally {
      setClaiming(false);
    }
  };

  const handlePromotionsIntroClose = async () => {
    setShowPromotionsIntro(false);
    await finishOnboarding(true, redeemedReward);
  };

  const handlePromotionsIntroView = async () => {
    setShowPromotionsIntro(false);
    await finishOnboarding(false);
    onViewPromotions?.();
  };

  if (loading || !data) {
    return null;
  }

  if (showPromotionsIntro) {
    return (
      <PromotionsIntroModal
        onClose={handlePromotionsIntroClose}
        onViewPromotions={handlePromotionsIntroView}
      />
    );
  }

  const welcomeRewards = data.welcome_rewards || [];
  const displayName = username || w.friendFallback;
  const isClubDiscountWelcome = welcomeRewards.some(
    (reward) => reward.fulfillment_mode === 'club_discount'
  );
  const welcomePoints = data.welcome_points || 0;
  const selectedReward = welcomeRewards.find((reward) => reward.id === selectedRewardId);
  const rewardGridClass =
    welcomeRewards.length === 1
      ? 'grid-cols-1'
      : welcomeRewards.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-3';

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center overflow-y-auto overscroll-contain p-4 safe-top safe-bottom sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-secondary-950/55 backdrop-blur-[6px]"
      />

      {confettiActive ? (
        <div className="pointer-events-none fixed inset-0 z-[310]">
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={120}
            gravity={0.28}
            colors={['#f97316', '#fb923c', '#fdba74', '#ffffff']}
          />
        </div>
      ) : null}

      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="welcome-step-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-onboarding-title"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[320] my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-secondary-200/70 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.22)]   sm:max-w-2xl"
          >
            <div className="px-6 py-8 sm:px-12 sm:py-12">
              <StepDots current={1} />

              <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-600  sm:text-xs">
                {w.eyebrow}
              </p>
              <h2
                id="welcome-onboarding-title"
                className="mt-3 text-center text-2xl font-semibold tracking-tight text-secondary-900  sm:text-3xl"
              >
                {formatMessage(w.title, { name: displayName })}
              </h2>
              <p className="mx-auto mt-3 max-w-md text-center text-sm leading-relaxed text-secondary-600  sm:mt-4 sm:max-w-lg sm:text-base">
                {isClubDiscountWelcome
                  ? formatMessage(w.subtitleElevate, { points: welcomePoints.toLocaleString() })
                  : w.subtitle}
              </p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12, duration: 0.3 }}
                className="mx-auto mt-8 w-full max-w-sm rounded-xl border border-secondary-200/80 bg-secondary-50/80 px-6 py-7 text-center   sm:mt-10 sm:max-w-md sm:px-8 sm:py-9"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-500  sm:text-xs">
                  {w.bonusLabel}
                </p>
                <p className="mt-3 flex items-baseline justify-center gap-1 text-4xl font-semibold tabular-nums tracking-tight text-secondary-900  sm:mt-4 sm:text-5xl">
                  <span className="text-orange-500">+</span>
                  <AnimatedStatNumber value={data.welcome_points || 0} />
                </p>
                <p className="mt-2 text-sm text-secondary-500  sm:text-[15px]">
                  {w.pointsGifted}
                </p>
              </motion.div>

              <div className="mt-8 flex flex-col items-center gap-3 sm:mt-10">
                <Button
                  onClick={handleAcceptWelcome}
                  size="large"
                  icon={ArrowRightIcon}
                  iconPosition="right"
                  className="w-full max-w-sm sm:max-w-md"
                >
                  {w.acceptBonus}
                </Button>
                <button
                  type="button"
                  onClick={() => onComplete?.()}
                  className="text-sm font-medium text-secondary-500 transition hover:text-secondary-800  "
                >
                  {t.ux?.onboarding?.exploreFirst || 'Explore dashboard first'}
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="welcome-step-2"
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-reward-title"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[320] my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-secondary-200/70 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.22)]   sm:max-w-3xl"
          >
            <div className="overflow-hidden px-5 py-6 sm:px-8 sm:py-8">
              <StepDots current={2} />

              <div className="mx-auto flex w-full flex-col items-center text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-600  sm:text-xs">
                  {w.readyEyebrow}
                </p>
                <h2
                  id="welcome-reward-title"
                  className="mt-2 text-xl font-semibold tracking-tight text-secondary-900  sm:text-2xl"
                >
                  {w.rewardReadyTitle}
                </h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-secondary-600 ">
                  {isClubDiscountWelcome
                    ? formatMessage(w.chooseRewardHintElevate, { points: welcomePoints.toLocaleString() })
                    : formatMessage(w.rewardReadySubtitle, {
                        points: data.current_points || 0,
                      })}
                </p>
              </div>

              <div className={`mx-auto mt-5 grid w-full gap-4 sm:mt-6 ${rewardGridClass}`}>
                {welcomeRewards.map((reward, index) => (
                  <motion.div
                    key={reward.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06, duration: 0.25 }}
                    className="w-full"
                  >
                    <WelcomeRewardOption
                      reward={reward}
                      selected={selectedRewardId === reward.id}
                      onSelect={setSelectedRewardId}
                      formatMessage={formatMessage}
                      pointsBadgeKey={w.pointsBadge}
                    />
                  </motion.div>
                ))}
              </div>

              {selectedReward && welcomeRewards.length > 1 ? (
                <p className="mx-auto mt-4 max-w-xl text-center text-sm text-secondary-600 ">
                  {formatMessage(w.selectedRewardLabel, { reward: selectedReward.name })}
                </p>
              ) : null}

              <div className="mt-5 flex flex-col items-center gap-2 sm:mt-6">
                <Button
                  disabled={!selectedRewardId || claiming}
                  loading={claiming}
                  onClick={handleRedeemNow}
                  size="large"
                  icon={GiftIcon}
                  className="w-full max-w-sm sm:max-w-md"
                >
                  {claiming ? w.claimingReward : w.redeemNow}
                </Button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm font-medium text-secondary-500 transition hover:text-secondary-800  "
                >
                  {w.backToBonus || 'Back'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ClientWelcomeOnboarding;
